import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ncbClient } from '../utils/ncbClient';
import { dropboxClient } from '../utils/dropboxClient';
import { openaiClient } from '../utils/openaiClient';

const { FiPlay, FiCode, FiCheck, FiDatabase, FiCloud, FiCpu } = FiIcons;

function ApiTestForm({ apiType }) {
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [customParams, setCustomParams] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const ncbEndpoints = [
    { id: 'create', label: 'Create User', method: 'POST', path: '/create/users' },
    { id: 'read', label: 'Read Users', method: 'GET', path: '/read/users' },
    { id: 'readById', label: 'Read User by ID', method: 'GET', path: '/read/users/{id}' },
    { id: 'search', label: 'Search Users', method: 'POST', path: '/search/users' },
    { id: 'update', label: 'Update User', method: 'PUT', path: '/update/users/{id}' },
    { id: 'delete', label: 'Delete User', method: 'DELETE', path: '/delete/users/{id}' }
  ];

  const dropboxEndpoints = [
    { id: 'listFiles', label: 'List Files', method: 'POST', path: '/files/list_folder' },
    { id: 'uploadFile', label: 'Upload File', method: 'POST', path: '/files/upload' },
    { id: 'downloadFile', label: 'Download File', method: 'POST', path: '/files/download' },
    { id: 'refreshToken', label: 'Refresh Token', method: 'POST', path: '/oauth2/token' }
  ];

  const openaiEndpoints = [
    { id: 'chat', label: 'Chat Completions', method: 'POST', path: '/chat' },
    { id: 'completion', label: 'Text Completions', method: 'POST', path: '/completions' },
    { id: 'embedding', label: 'Create Embeddings', method: 'POST', path: '/embeddings' },
    { id: 'models', label: 'List Models', method: 'GET', path: '/models' }
  ];

  const endpoints = apiType === 'ncb' ? ncbEndpoints : 
                   apiType === 'dropbox' ? dropboxEndpoints : 
                   openaiEndpoints;

  const getExampleBody = (endpointId) => {
    if (apiType === 'ncb') {
      switch (endpointId) {
        case 'create':
        case 'search':
          return JSON.stringify({
            "username": "testuser",
            "password": "testpass123"
          }, null, 2);
        case 'update':
          return JSON.stringify({
            "username": "updateduser",
            "password": "newpass123"
          }, null, 2);
        default:
          return '';
      }
    } else if (apiType === 'dropbox') {
      switch (endpointId) {
        case 'listFiles':
          return JSON.stringify({
            "path": "",
            "recursive": false
          }, null, 2);
        case 'uploadFile':
          return JSON.stringify({
            "path": "/test-file.txt",
            "mode": "add",
            "autorename": true
          }, null, 2);
        case 'refreshToken':
          return JSON.stringify({
            "refreshToken": "your-refresh-token"
          }, null, 2);
        default:
          return '';
      }
    } else {
      // OpenAI
      const samples = openaiClient.getSampleRequests();
      return samples[endpointId] ? JSON.stringify(samples[endpointId], null, 2) : '';
    }
  };

  const handleEndpointChange = (endpointId) => {
    setSelectedEndpoint(endpointId);
    setRequestBody(getExampleBody(endpointId));
  };

  const handleUseExample = () => {
    setRequestBody(getExampleBody(selectedEndpoint));
  };

  const handleClear = () => {
    setRequestBody('');
    setCustomParams('');
  };

  const handleExecuteRequest = async () => {
    if (!selectedEndpoint) return;
    
    setIsLoading(true);
    try {
      const endpoint = endpoints.find(e => e.id === selectedEndpoint);
      
      if (apiType === 'ncb') {
        const body = requestBody ? JSON.parse(requestBody) : null;
        const params = customParams ? JSON.parse(customParams) : {};
        await ncbClient.request(endpoint.method, endpoint.path, body, params);
      } else if (apiType === 'dropbox') {
        const body = requestBody ? JSON.parse(requestBody) : null;
        await dropboxClient.request(endpoint.method, endpoint.path, body);
      } else {
        // OpenAI
        const body = requestBody ? JSON.parse(requestBody) : null;
        await openaiClient.request(endpoint.method, endpoint.path, body);
      }
    } catch (error) {
      console.error('Request failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateCurlCommand = () => {
    if (!selectedEndpoint) return '';
    
    const endpoint = endpoints.find(e => e.id === selectedEndpoint);
    
    let url;
    if (apiType === 'ncb') {
      const baseUrl = import.meta.env.VITE_NCB_BASE_URL || 'https://openapi.nocodebackend.com';
      const instanceId = import.meta.env.VITE_NCB_INSTANCE_ID || '53830_users';
      url = `${baseUrl}${endpoint.path}?Instance=${instanceId}`;
    } else if (apiType === 'dropbox') {
      url = `https://api.dropboxapi.com/2${endpoint.path}`;
    } else {
      // OpenAI
      url = `${window.location.origin}/api/openai${endpoint.path}`;
    }
    
    let curlCommand = `curl -X ${endpoint.method} "${url}" \\\n`;
    curlCommand += `  -H "Content-Type: application/json" \\\n`;
    
    if (apiType === 'ncb') {
      curlCommand += `  -H "Authorization: Bearer YOUR_NCB_API_KEY" \\\n`;
    } else if (apiType === 'dropbox') {
      curlCommand += `  -H "Authorization: Bearer YOUR_DROPBOX_ACCESS_TOKEN" \\\n`;
    } else {
      curlCommand += `  -H "Authorization: Bearer YOUR_OPENAI_API_KEY" \\\n`;
    }
    
    if ((endpoint.method === 'POST' || endpoint.method === 'PUT') && requestBody) {
      const escapedBody = requestBody.replace(/'/g, "\\'");
      curlCommand += `  -d '${escapedBody}'`;
    }
    
    return curlCommand;
  };

  const handleCopyCurl = async () => {
    try {
      await navigator.clipboard.writeText(generateCurlCommand());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getEnvStatus = (envVar) => {
    const value = import.meta.env[envVar];
    return value ? { status: 'Set', color: 'text-green-600' } : { status: 'Missing', color: 'text-red-600' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {apiType === 'ncb' ? 'NoCodeBackend API Tester' : 
             apiType === 'dropbox' ? 'Dropbox API Tester' : 
             'OpenAI API Tester'}
          </h2>
          <div className="flex items-center space-x-2 mt-1">
            <div className={`w-2 h-2 rounded-full ${
              apiType === 'ncb' ? 'bg-blue-500' : 
              apiType === 'dropbox' ? 'bg-green-500' : 
              'bg-purple-500'
            }`}></div>
            <span className="text-sm text-gray-500">
              {apiType === 'ncb' ? 'NCB' : 
               apiType === 'dropbox' ? 'Dropbox' : 
               'OpenAI'}
            </span>
          </div>
        </div>
        <SafeIcon 
          icon={
            apiType === 'ncb' ? FiDatabase : 
            apiType === 'dropbox' ? FiCloud : 
            FiCpu
          } 
          className={`text-2xl ${
            apiType === 'ncb' ? 'text-blue-600' : 
            apiType === 'dropbox' ? 'text-green-600' : 
            'text-purple-600'
          }`} 
        />
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Endpoint
          </label>
          <select 
            value={selectedEndpoint} 
            onChange={(e) => handleEndpointChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Choose an endpoint...</option>
            {endpoints.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.method} - {endpoint.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex space-x-2">
          <button 
            onClick={handleUseExample}
            disabled={!selectedEndpoint}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Use Example Body
          </button>
          <button 
            onClick={handleClear}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Request Body (JSON)
          </label>
          <textarea 
            value={requestBody}
            onChange={(e) => setRequestBody(e.target.value)}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono text-sm"
            placeholder="Enter JSON request body..."
          />
        </div>

        {apiType === 'ncb' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Query Parameters (JSON)
            </label>
            <textarea 
              value={customParams}
              onChange={(e) => setCustomParams(e.target.value)}
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono text-sm"
              placeholder='{"param1": "value1", "param2": "value2"}'
            />
          </div>
        )}

        <div className="flex space-x-3">
          <button 
            onClick={handleExecuteRequest}
            disabled={!selectedEndpoint || isLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <SafeIcon icon={FiPlay} />
            )}
            <span>{isLoading ? 'Running...' : 'Run Request'}</span>
          </button>
          
          <button 
            onClick={handleCopyCurl}
            disabled={!selectedEndpoint}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SafeIcon icon={copySuccess ? FiCheck : FiCode} />
            <span>{copySuccess ? 'Copied!' : 'Copy cURL'}</span>
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Environment Status</h3>
          <div className="space-y-2">
            {apiType === 'ncb' ? (
              <>
                {['VITE_NCB_BASE_URL', 'VITE_NCB_INSTANCE_ID', 'VITE_NCB_API_KEY'].map((envVar) => {
                  const { status, color } = getEnvStatus(envVar);
                  return (
                    <div key={envVar} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{envVar}</span>
                      <span className={`text-sm font-medium ${color}`}>
                        {status === 'Set' ? '✓ Set' : '✗ Missing'}
                      </span>
                    </div>
                  );
                })}
              </>
            ) : apiType === 'dropbox' ? (
              <>
                {['VITE_DROPBOX_APP_KEY'].map((envVar) => {
                  const { status, color } = getEnvStatus(envVar);
                  return (
                    <div key={envVar} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{envVar}</span>
                      <span className={`text-sm font-medium ${color}`}>
                        {status === 'Set' ? '✓ Set' : '✗ Missing'}
                      </span>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-500 mt-2">
                  Dropbox APP_SECRET and REFRESH_TOKEN must be set in Cloudflare Pages Functions, not in frontend code.
                </p>
              </>
            ) : (
              <>
                {['VITE_OPENAI_API_KEY'].map((envVar) => {
                  const { status, color } = getEnvStatus(envVar);
                  return (
                    <div key={envVar} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{envVar}</span>
                      <span className={`text-sm font-medium ${color}`}>
                        {status === 'Set' ? '✓ Set' : '✗ Missing'}
                      </span>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-500 mt-2">
                  OPENAI_API_KEY must be set in Cloudflare Pages Functions environment for production use.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiTestForm;